import stylesheet from './style.scss?inline';

import CustomComponent from "../../base/customComponent/CustomComponent";
import {getIconStyles} from "../../util/lazy";

class NXTButton extends CustomComponent {
    private static readonly passThroughArguments = ['href', 'target', 'disabled',];

    constructor() {
        super();
        this.customStyle = stylesheet;
        void this.appendStyleLazy(getIconStyles);
    }

    get buttonTagName(): 'a' | 'button' {
        if (this.hasAttribute('tag')) {
            const tag = this.getAttribute('tag');
            if (['a', 'button'].includes(tag)) {
                return tag as ('a' | 'button');
            }
        }
        return 'button';
    }

    passThroughProperties(element: HTMLAnchorElement | HTMLButtonElement) {
        this.getAttributeNames()
            .filter(existingArgument => NXTButton.passThroughArguments.includes(existingArgument))
            .forEach(attributeName => {
                element.setAttribute(attributeName, this.getAttribute(attributeName));
            });
    }

    isValidButtonSize(size: string): boolean {
        return ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'icon'].includes(size);
    }

    get buttonSize(): string {
        if (this.hasAttribute('size')) {
            const size = this.getAttribute('size');
            if (this.isValidButtonSize(size)) {
                return size;
            }
        }
        return 'lg';
    }

    get buttonType(): string {
        if (this.hasAttribute('type')) {
            const type = this.getAttribute('type');
            if (['primary', 'secondary', 'tertiary', 'danger', 'link', 'link-secondary'].includes(type)) {
                return type;
            }
        }
        return 'primary';
    }

    get buttonIconLeft(): string | undefined {
        if (this.hasAttribute('icon-left')) {
            return this.getAttribute('icon-left');
        }
        return undefined;
    }

    get buttonIconRight(): string | undefined {
        if (this.hasAttribute('icon-right')) {
            return this.getAttribute('icon-right');
        }
        return undefined;
    }

    get buttonWidthVariantSm(): 'block' | 'icon-only' | 'shrink' | 'grow' | 'static' {
        if (this.hasAttribute('block') && this.getAttribute('block') !== 'false') {
            return 'block';
        } else if (this.hasAttribute('icon-only') && this.getAttribute('icon-only') !== 'false') {
            return 'icon-only';
        } else if (this.hasAttribute('shrink') && this.getAttribute('shrink') !== 'false') {
            return 'shrink';
        } else if (!this.hasAttribute('grow') || this.getAttribute('grow') !== 'false') {
            return 'grow';
        } else {
            return 'static';
        }
    }

    getButtonWidthBreakpointVariants(): string[] {
        const additionalClasses: string[] = [];
        for (const breakpoint of ['md', 'lg', 'xl', 'xxl']) {
            const attributeName = `${breakpoint}`;
            if (this.hasAttribute(attributeName)) {
                additionalClasses.push(`button-${this.getAttribute(attributeName)}-${breakpoint}-up`);
            }
        }
        return additionalClasses;
    }

    get buttonGrow(): boolean {
        return this.buttonWidthVariantSm === 'grow';
    }

    get buttonBlock(): boolean {
        return this.buttonWidthVariantSm === 'block';
    }

    get buttonShrink(): boolean {
        return this.buttonWidthVariantSm === 'shrink';
    }

    get iconOnly(): boolean {
        return this.buttonWidthVariantSm === 'icon-only';
    }

    getButtonBreakpointSizes(): string[] {
        const additionalClasses: string[] = [];
        for (const breakpoint of ['md', 'lg', 'xl', 'xxl']) {
            const attributeName = `size-${breakpoint}`;
            if (this.hasAttribute(attributeName) && this.isValidButtonSize(this.getAttribute(attributeName))) {
                additionalClasses.push(`button-${this.getAttribute(attributeName)}-${breakpoint}-up`);
            }
        }
        return additionalClasses;
    }

    getButtonClasses(): string[] {
        // Default classes
        const classList = ['button', `button-${this.buttonSize}`, ...this.getButtonBreakpointSizes(), `button-${this.buttonType}`,];
        // Icons
        if (this.buttonIconLeft) {
            classList.push('button-icon-left', `button-icon-left-${this.buttonIconLeft}`);
        }
        if (this.buttonIconRight) {
            classList.push('button-icon-right', `button-icon-right-${this.buttonIconRight}`);
        }
        // Grow or Block (or none)
        if (this.buttonGrow) {
            classList.push(`button-grow`);
        } else if (this.buttonBlock) {
            classList.push(`button-block`);
        } else if (this.buttonShrink) {
            classList.push(`button-shrink`);
        } else if (this.iconOnly) {
            classList.push(`button-icon-only`);
        }
        classList.push(...this.getButtonWidthBreakpointVariants());

        // Return list
        return classList;
    }

    handleTargetOpen(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const target = document.getElementById(this.getAttribute('target-open')) as unknown as { open?: unknown };
        if (target && typeof target.open === 'function') {
            (target.open as () => void).call(target);
        }
    }

    render() {
        const element = document.createElement(this.buttonTagName);
        element.classList.add(...this.getButtonClasses());
        this.passThroughProperties(element);
        element.innerHTML = `<slot></slot>`;
        if (this.hasAttribute('target-open')) {
            element.addEventListener('click', (event) => this.handleTargetOpen(event));
        } else if (this.hasAttribute('trigger-event')) {
            element.addEventListener('click', () => {
                this.dispatchEvent(new CustomEvent(this.getAttribute('trigger-event'), {bubbles: true, composed: true}))
            });
        }
        return element;
    }

}

customElements.define('nxt-button', NXTButton);
